import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { surveyAPI, adminAPI } from '../services/api'
import { sumOthersLineAmounts } from '../utils/invoiceFinancialCalculations'

const CHARGE_LINE_KEYS = ['survey_fee', 'travel_expenses', 'incentive', 'others']
const CORE_CHARGE_LINE_KEYS = CHARGE_LINE_KEYS.filter((k) => k !== 'others')

const Invoice = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [invoiceData, setInvoiceData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeEditCell, setActiveEditCell] = useState(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [selectedItems, setSelectedItems] = useState({
    survey_fee: false,
    travel_expenses: false,
    incentive: false,
    others: false,
    discount: false,
    tax: false,
    recovery: false
  })
  // State for edited invoice items
  const [editedItems, setEditedItems] = useState({})
  // State for edited invoice details
  const [editedDetails, setEditedDetails] = useState({})
  // State for edited discount (stored as string)
  const [editedDiscount, setEditedDiscount] = useState(null)
  // State for edited total amount due
  const [editedTotalAmount, setEditedTotalAmount] = useState(null)
  // State for edited tax amount
  const [editedTaxAmount, setEditedTaxAmount] = useState(null)
  const addressSpanRef = useRef(null)
  const titleMeasureRef = useRef(null)
  const [titleWidth, setTitleWidth] = useState(null)
  const [bankInfoList, setBankInfoList] = useState([])
  const [selectedBankInfoId, setSelectedBankInfoId] = useState(null)
  const [companyInfoList, setCompanyInfoList] = useState([])
  const [selectedCompanyId, setSelectedCompanyId] = useState(null)
  // Currency state - default to USD, persist across edit mode toggles
  const [currency, setCurrency] = useState('USD')
  // Others breakdown rows (invoice_meta.extra_lines)
  const [emptyRows, setEmptyRows] = useState([])
  const [saveStatus, setSaveStatus] = useState('idle')
  const [saveError, setSaveError] = useState(null)
  const dirtyRef = useRef(false)
  const suspendAutoSaveRef = useRef(false)

  const markDirty = () => {
    dirtyRef.current = true
    setHasUnsavedChanges(true)
  }

  const isCellActive = (cellId) => activeEditCell === cellId

  const activateCell = (cellId) => setActiveEditCell(cellId)

  const wrapClickToEdit = (cellId, node, className = '') => (
    <span
      role="button"
      tabIndex={0}
      className={`cursor-text hover:bg-amber-50/60 rounded inline-block ${className}`.trim()}
      onClick={() => activateCell(cellId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          activateCell(cellId)
        }
      }}
    >
      {node}
    </span>
  )

  const loadInvoiceData = useCallback(async () => {
    try {
      suspendAutoSaveRef.current = true
      setLoading(true)
      const response = await surveyAPI.getInvoiceData(id)
      if (response.data.success) {
        // Clear company_info and bank_info so user must select manually
        const invoiceData = {
          ...response.data,
          company_info: {
            name: '',
            address: '',
            tel: '',
            email: '',
            web: '',
          },
          bank_info: null
        }
        setInvoiceData(invoiceData)
        // Initialize edited items with original values as strings
        // Preserve decimal places for amounts (e.g., 4000.00 stays as "4000.00")
        const meta = response.data.invoice_meta || response.data.survey?.invoice_meta || {}
        const lineDesc = meta.line_descriptions || {}
        const items = {
          ...(response.data.available_items || {}),
          others: response.data.available_items?.others ?? {
            description: 'Additional Charges',
            amount: 0,
          },
        }
        const initialEdited = {}
        Object.keys(items).forEach((key) => {
          const amount = items[key].amount
          let amountStr = '0.00'
          if (amount !== null && amount !== undefined) {
            if (typeof amount === 'number') {
              amountStr = amount.toFixed(2)
            } else {
              const numValue = parseFloat(amount)
              amountStr = !isNaN(numValue) ? numValue.toFixed(2) : String(amount)
            }
          }
          let description = String(items[key].description || '')
          if (key === 'survey_fee' && response.data.survey?.survey_type) {
            description = `${description} (${response.data.survey.survey_type})`
          }
          if (lineDesc[key]) {
            description = String(lineDesc[key])
          }
          initialEdited[key] = { description, amount: amountStr }
        })
        let breakdownRows = Array.isArray(meta.extra_lines)
          ? meta.extra_lines.map((row, idx) => ({
              id: row.id != null ? row.id : `loaded-${idx}-${Date.now()}`,
              description: row.description != null ? String(row.description) : '',
              amount: row.amount != null ? String(row.amount) : '0.00',
              selected: row.selected !== false,
            }))
          : []

        if (breakdownRows.length === 0) {
          const legacyOthers = parseFloat(String(initialEdited.others?.amount ?? '0'))
          if (Number.isFinite(legacyOthers) && legacyOthers !== 0) {
            breakdownRows = [
              {
                id: `invoice-legacy-others-${Date.now()}`,
                description: '',
                amount: legacyOthers.toFixed(2),
                selected: true,
              },
            ]
          }
        }

        if (breakdownRows.length > 0) {
          const othersBreakdownSum = sumOthersLineAmounts(breakdownRows, { onlySelected: true })
          initialEdited.others = {
            ...initialEdited.others,
            amount: othersBreakdownSum.toFixed(2),
          }
        }

        setEditedItems(initialEdited)
        setEmptyRows(breakdownRows)

        const defaultSel = {
          survey_fee: false,
          travel_expenses: false,
          incentive: false,
          others: false,
          discount: false,
          tax: false,
          recovery: false,
        }
        setSelectedItems({
          ...defaultSel,
          ...(meta.selectedItems || {}),
        })
        if (meta.currency) {
          setCurrency(meta.currency)
        }

        const lo = meta.label_overrides || {}
        const labelState = {}
        if (lo.discount_label !== undefined) labelState.discount_label = lo.discount_label
        if (lo.tax_label !== undefined) labelState.tax_label = lo.tax_label
        if (lo.total_label !== undefined) labelState.total_label = lo.total_label
        setEditedDetails(Object.keys(labelState).length ? labelState : {})

        if (meta.total_override != null && meta.total_override !== '') {
          setEditedTotalAmount(String(meta.total_override))
        } else {
          setEditedTotalAmount(null)
        }

        dirtyRef.current = false
        setHasUnsavedChanges(false)
        setSaveStatus('idle')
      } else {
        setError(response.data.message || 'Failed to load invoice data')
      }
    } catch (err) {
      console.error('Error loading invoice:', err)
      setError(err.response?.data?.message || 'Failed to load invoice data')
    } finally {
      setLoading(false)
      suspendAutoSaveRef.current = false
    }
  }, [id])

  useEffect(() => {
    if (id) {
      loadInvoiceData()
    }
  }, [id, loadInvoiceData])

  // Load bank info directly (independent from bank accounts)
  useEffect(() => {
    const loadBankInfo = async () => {
      try {
        const response = await adminAPI.getBankInfo()
        if (response.data?.data) {
          console.log('Loaded bank info:', response.data.data)
          setBankInfoList(response.data.data)
        } else {
          console.warn('No bank info data in response:', response.data)
        }
      } catch (err) {
        console.error('Error loading bank info:', err)
      }
    }
    loadBankInfo()
  }, [])

  // Initialize selected bank account to null - user must select manually

  const handleCompanyChange = useCallback((companyId) => {
    setSelectedCompanyId(companyId)
    const selectedCompany = companyInfoList.find(comp => comp.id === companyId)
    console.log('Selected company data:', selectedCompany) // Debug log
    if (selectedCompany && invoiceData) {
      // Update only company info, not bank info
      // Get Email and Web from custom_fields structure (mapped from fixed fields)
      const email = selectedCompany.company_email || ''
      const web = selectedCompany.company_web || ''
      setInvoiceData(prev => ({
        ...prev,
        company_info: {
          name: selectedCompany.company_name,
          address: selectedCompany.company_address,
          tel: selectedCompany.company_tel,
          email: email,
          web: web,
        }
      }))
    }
  }, [companyInfoList, invoiceData])

  const getDetailValue = useCallback((field) => {
    // Always use edited values if they exist (regardless of edit mode)
    // This allows edited values to persist in view mode (temporary changes only)
    if (editedDetails[field] !== undefined) {
      return String(editedDetails[field] || '')
    }
    // Handle nested fields
    const fieldMap = {
      'client_name': invoiceData?.survey?.client_name,
      'client_address': invoiceData?.client_address,
      'client_email': invoiceData?.client_email,
      'operator': invoiceData?.survey?.operator,
      'vessel_name': invoiceData?.survey?.vessel_name,
      'client_ref_no': invoiceData?.survey?.client_ref_no,
      'survey_country': invoiceData?.survey?.survey_country,
      'survey_port': invoiceData?.survey?.survey_port,
      'invoice_number': invoiceData?.invoice_number,
      'invoice_title': 'INVOICE'
    }
    return String(fieldMap[field] || '')
  }, [editedDetails, invoiceData])

  // Load company info list
  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const response = await adminAPI.getCompanyInfo()
        if (response.data?.data) {
          setCompanyInfoList(response.data.data)
          // Remove auto-selection - user must manually select company
        } else {
          console.warn('No company info data in response:', response.data)
        }
      } catch (err) {
        console.error('Error loading company info:', err)
      }
    }
    loadCompanyInfo()
  }, [])

  // Measure title width when editing invoice title
  useEffect(() => {
    if (activeEditCell === 'invoice_title') {
      const tempSpan = document.createElement('span')
      tempSpan.className = 'invoice-title mb-0 text-left'
      tempSpan.style.position = 'absolute'
      tempSpan.style.visibility = 'hidden'
      tempSpan.style.whiteSpace = 'nowrap'
      tempSpan.style.fontSize = '2.5rem'
      tempSpan.style.fontWeight = 'bold'
      tempSpan.style.fontFamily = window.getComputedStyle(document.body).fontFamily
      const titleText = getDetailValue('invoice_title') || 'INVOICE'
      tempSpan.textContent = titleText
      document.body.appendChild(tempSpan)
      const width = tempSpan.offsetWidth
      document.body.removeChild(tempSpan)
      setTitleWidth(Math.max(width + 20, 150))
    }
  }, [activeEditCell, invoiceData, getDetailValue])


  const handleItemEdit = (itemKey, field, value) => {
    if (itemKey === 'others' && field === 'amount') return
    markDirty()
    setEditedItems((prev) => ({
      ...prev,
      [itemKey]: {
        ...prev[itemKey],
        [field]: value,
      },
    }))
  }

  const handleDiscountWorkspaceChange = (value) => {
    markDirty()
    setEditedDiscount(value)
  }

  const handleDetailEdit = (field, value) => {
    const workspaceLabelFields = ['discount_label', 'tax_label', 'total_label']
    if (workspaceLabelFields.includes(field)) {
      markDirty()
    }
    setEditedDetails((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  /** Nested rows under Others -> stored in invoice_meta.extra_lines */
  const addOthersBreakdownLine = () => {
    if (!selectedItems.others) return
    markDirty()
    setEmptyRows((prev) => {
      const newId = Date.now()
      if (prev.length > 0) {
        return [...prev, { id: newId, description: '', amount: '0.00', selected: true }]
      }
      const seeded = parseFloat(getItemValue('others', 'amount')) || 0
      return [
        {
          id: newId,
          description: '',
          amount: seeded > 0 ? seeded.toFixed(2) : '0.00',
          selected: true,
        },
      ]
    })
  }

  const removeOthersBreakdownLine = (lineId) => {
    markDirty()
    setEmptyRows((prev) => prev.filter((row) => row.id !== lineId))
  }

  const handleEmptyRowEdit = (id, field, value) => {
    markDirty()
    setEmptyRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)))
  }

  const handleEmptyRowToggle = (lineId) => {
    markDirty()
    setEmptyRows((prev) =>
      prev.map((row) =>
        row.id === lineId ? { ...row, selected: row.selected === false ? true : false } : row
      )
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    // Parse the date string as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day) // Local date constructor
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric'
    })
  }

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return '-'
    const numValue = parseFloat(value).toFixed(2)
    // Dollar symbol at beginning, Euro symbol at end, SGD at beginning
    if (currency === 'EUR') {
      return `${numValue}€`
    } else if (currency === 'SGD') {
      return `S$${numValue}`
    } else {
      return `USD ${numValue}`
    }
  }

  const formatChargeAlways = (value) => {
    const raw = parseFloat(value)
    const n = (Number.isFinite(raw) ? raw : 0).toFixed(2)
    if (currency === 'EUR') return `${n}€`
    if (currency === 'SGD') return `S$${n}`
    return `USD ${n}`
  }

  const formatFloat = (value, decimals = 3) => {
    if (value === null || value === undefined) return '-'
    return parseFloat(value).toFixed(decimals)
  }

  const roundTo2 = (value) => {
    // Avoid common floating point issues (e.g. 0.1 * 0.2)
    const n = Number(value)
    if (!Number.isFinite(n)) return 0
    return Math.round((n + Number.EPSILON) * 100) / 100
  }

  // Measure actual rendered width of text with given styling
  const measureTextWidth = (text, styles = {}) => {
    const tempSpan = document.createElement('span')
    Object.assign(tempSpan.style, {
      position: 'absolute',
      visibility: 'hidden',
      whiteSpace: 'nowrap',
      fontFamily: window.getComputedStyle(document.body).fontFamily,
      ...styles
    })
    tempSpan.textContent = text || ''
    document.body.appendChild(tempSpan)
    const width = tempSpan.offsetWidth
    document.body.removeChild(tempSpan)
    return width
  }

  const calculateTotals = () => {
    if (!invoiceData) return { subtotal: 0, tax: 0, discount: 0, total: 0 }
    
    let subtotal = 0
    // Use edited items if available, otherwise use original
    const items = Object.keys(editedItems).length > 0 ? editedItems : invoiceData.available_items || {}
    
    // Parse string values to numbers for calculation
    if (selectedItems.survey_fee && items.survey_fee) {
      const amount = parseFloat(items.survey_fee.amount) || 0
      subtotal += amount
    }
    if (selectedItems.travel_expenses && items.travel_expenses) {
      const amount = parseFloat(items.travel_expenses.amount) || 0
      subtotal += amount
    }
    if (selectedItems.incentive && items.incentive) {
      const amount = parseFloat(items.incentive.amount) || 0
      subtotal += amount
    }
    if (selectedItems.others && items.others) {
      subtotal += sumOthersLineAmounts(emptyRows, { onlySelected: true })
    }
    
    const discount = selectedItems.discount ? (parseFloat(editedDiscount) || parseFloat(invoiceData.discount) || 0) : 0
    // Tax is 8% of subtotal (before discount) — matches invoice API tax_rate
    const taxRate = selectedItems.tax ? 8 : 0
    const tax = subtotal * (taxRate / 100)
    const total = subtotal - discount
    
    return { subtotal, tax, discount, total }
  }

  const getItemValue = (itemKey, field) => {
    // Always use edited values if they exist (regardless of edit mode)
    // This allows edited values to persist in view mode (temporary changes only)
    if (editedItems[itemKey] && editedItems[itemKey][field] !== undefined) {
      const editedValue = editedItems[itemKey][field]
      if (field === 'amount') {
        // Preserve decimal format for amounts
        const numValue = parseFloat(editedValue)
        if (!isNaN(numValue)) {
          return numValue.toFixed(2)
        }
        return String(editedValue || '0.00')
      }
      return String(editedValue || '')
    }
    // Fall back to original value if no edits exist
    const value = invoiceData?.available_items?.[itemKey]?.[field]
    if (field === 'amount') {
      // Preserve decimal format when not in edit mode (for display)
      if (value !== null && value !== undefined) {
        if (typeof value === 'number') {
          return value.toFixed(2)
        } else {
          // If string, try to parse and format
          const numValue = parseFloat(value)
          if (!isNaN(numValue)) {
            return numValue.toFixed(2)
          }
          return String(value)
        }
      }
      return '0.00'
    }
    return String(value || '')
  }

  useEffect(() => {
    const sum = sumOthersLineAmounts(emptyRows, { onlySelected: true })
    const s = sum.toFixed(2)
    setEditedItems((prev) => ({
      ...prev,
      others: {
        ...(prev.others || { description: 'Additional Charges', amount: '0.00' }),
        amount: s,
      },
    }))
  }, [emptyRows])

  const buildInvoiceSavePayload = () => {
    if (!invoiceData?.survey) return null
    const metaBase = invoiceData.invoice_meta || invoiceData.survey.invoice_meta || {}
    return {
      survey_fee: parseFloat(getItemValue('survey_fee', 'amount')) || null,
      expenses: parseFloat(getItemValue('travel_expenses', 'amount')) || null,
      recovery: parseFloat(getItemValue('incentive', 'amount')) || null,
      others: sumOthersLineAmounts(emptyRows, { onlySelected: true }),
      discount: selectedItems.discount
        ? parseFloat(editedDiscount ?? invoiceData.discount) || null
        : null,
      invoice_meta: {
        ...metaBase,
        selectedItems,
        currency,
        extra_lines: emptyRows.map(({ id, description, amount, selected }) => ({
          id,
          description,
          amount,
          selected: selected !== false,
        })),
        line_descriptions: {
          survey_fee: getItemValue('survey_fee', 'description'),
          travel_expenses: getItemValue('travel_expenses', 'description'),
          incentive: getItemValue('incentive', 'description'),
          others: getItemValue('others', 'description'),
        },
        label_overrides: {
          ...(editedDetails.discount_label !== undefined && {
            discount_label: editedDetails.discount_label,
          }),
          ...(editedDetails.tax_label !== undefined && { tax_label: editedDetails.tax_label }),
          ...(editedDetails.total_label !== undefined && {
            total_label: editedDetails.total_label,
          }),
        },
        total_override: editedTotalAmount,
      },
    }
  }

  const handleSaveInvoice = useCallback(async () => {
    if (!id || !invoiceData || suspendAutoSaveRef.current) return
    const payload = buildInvoiceSavePayload()
    if (!payload) return
    try {
      setSaveStatus('saving')
      setSaveError(null)
      const response = await surveyAPI.saveInvoice(id, payload)
      if (!response.data.success) {
        throw new Error(response.data.message || 'Save failed')
      }
      setSaveStatus('saved')
      dirtyRef.current = false
      setHasUnsavedChanges(false)
      await loadInvoiceData()
    } catch (err) {
      dirtyRef.current = true
      setHasUnsavedChanges(true)
      setSaveStatus('error')
      setSaveError(err.response?.data?.message || err.message || 'Save failed')
    }
  }, [
    id,
    invoiceData,
    editedItems,
    editedDetails,
    editedDiscount,
    editedTotalAmount,
    selectedItems,
    emptyRows,
    currency,
    loadInvoiceData,
  ])

  const getRecoveryValue = (field) => {
    const value = invoiceData?.survey?.[field]
    return value ? String(value) : ''
  }

  const renderRecoveryCell = (field, { isPrice = false } = {}) => {
    const raw = getRecoveryValue(field)
    return isPrice
      ? formatCurrency(parseFloat(raw) || 0)
      : formatFloat(parseFloat(raw) || 0)
  }

  // Read-only recovery display (legacy name kept for hot-reload compatibility)
  const renderRecoveryEditableCell = renderRecoveryCell

  const handlePrint = () => {
    const invoiceNumber = getDetailValue('invoice_number')
    const originalTitle = document.title
    document.title = `${invoiceNumber}.pdf`
    window.print()
    document.title = originalTitle
  }


  const handleItemToggle = (item) => {
    markDirty()
    setSelectedItems((prev) => ({
      ...prev,
      [item]: !prev[item],
    }))
  }

  const handleBankInfoChange = (bankInfoId) => {
    setSelectedBankInfoId(bankInfoId)
    const selectedBank = bankInfoList.find(bank => bank.id === bankInfoId)
    if (selectedBank && invoiceData) {
      // Update bank info using fixed fields (same as old version)
      const bankInfo = {
        account_holder: selectedBank.account_holder || '',
        bank_name: selectedBank.bank_name || '',
        bank_address: selectedBank.bank_address || '',
        swift: selectedBank.swift || '',
        bank_branch_code: selectedBank.bank_branch_code || '',
        account_number_sgd: selectedBank.account_number_sgd || '',
        account_number_usd: selectedBank.account_number_usd || ''
      }
      console.log('Setting bank info:', bankInfo) // Debug log
      
      setInvoiceData(prev => ({
        ...prev,
        bank_info: bankInfo
      }))
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-gray-900"></div>
          <p className="text-gray-600 mt-4">Loading invoice...</p>
        </div>
      </div>
    )
  }

  if (error || !invoiceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Invoice not found'}</p>
          <button
            onClick={() => navigate('/surveys')}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
          >
            Back to Surveys
          </button>
        </div>
      </div>
    )
  }

  const {
    survey,
    company_info,
    due_date,
    client_address,
    client_email,
    available_items: rawAvailableItems,
    bank_info,
  } = invoiceData
  const available_items = {
    ...(rawAvailableItems || {}),
    others: rawAvailableItems?.others ?? {
      description: 'Additional Charges',
      amount: 0,
    },
  }
  const totals = calculateTotals()
  const displayedTotalAmountDue =
    editedTotalAmount !== null ? (parseFloat(editedTotalAmount) || 0) : (totals.total || 0)
  // Use edited tax amount if available, otherwise calculate from total (only when not in edit mode)
  const displayedTaxAmount =
    editedTaxAmount !== null
      ? parseFloat(editedTaxAmount) || 0
      : selectedItems.tax
        ? totals.tax
        : roundTo2(displayedTotalAmountDue * 0.08)

  return (
    <div className="min-h-screen bg-gray-50 py-8 invoice-page">
      <style>{`
        @media print {
          /* Hide everything except invoice content */
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Hide navigation and all non-print elements */
          header,
          nav,
          .no-print,
          .print-hidden,
          button,
          .btn,
          .fixed,
          [class*="fixed"] {
            display: none !important;
          }
          
          /* Specifically hide the Layout header */
          body > div > header,
          body > div > nav,
          .print-hidden {
            display: none !important;
            visibility: hidden !important;
          }
          
          /* Hide main padding from Layout */
          main {
            padding-top: 0 !important;
            margin-top: 0 !important;
          }
          
          /* Remove all top spacing */
          .pt-24 {
            padding-top: 0 !important;
          }
          
          /* Remove all page margins - maximize space */
          @page {
            margin: 0.3cm !important;
            size: A4;
          }
          
          /* Container adjustments */
          .invoice-page {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          
          .container {
            max-width: 100% !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          
          /* Card adjustments */
          .bg-white {
            border: none !important;
            box-shadow: none !important;
            margin: 0 !important;
            padding: 0 !important;
            page-break-inside: avoid !important;
          }
          
          /* Reduce all padding and margins */
          .p-6 {
            padding: 0.5rem !important;
          }
          
          .py-8 {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          
          .mb-2 {
            margin-bottom: 0.15rem !important;
          }
          
          .mb-3 {
            margin-bottom: 0.25rem !important;
          }
          
          .mt-1 {
            margin-top: 0.1rem !important;
          }
          
          /* Header section - compact with spacing */
          .border-b-2 {
            border-bottom-width: 1px !important;
            padding: 0.5rem !important;
            margin-bottom: 1rem !important;
            padding-bottom: 0.8rem !important;
          }
          
          .border-b {
            border-bottom-width: 1px !important;
            padding: 0.5rem !important;
          }
          
          /* Add spacing after header in print */
          #invoice-header {
            margin-bottom: 1rem !important;
            padding-bottom: 0.8rem !important;
          }
          
          /* Add spacing before client section in print */
          .invoice-content-wrapper > div:first-child {
            margin-top: 0.8rem !important;
            padding-top: 0.8rem !important;
          }
          
          /* Invoice title and number - centered in print */
          #invoice-header .invoice-title {
            text-align: center !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          
          #invoice-header .text-left {
            text-align: center !important;
          }
          
          #invoice-header .text-center {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          
          #invoice-header .text-center p {
            text-align: center !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          
          /* 3-column header layout in print - last column wider */
          #invoice-header .grid[style*="grid-template-columns"] {
            display: grid !important;
            grid-template-columns: 1fr 1fr 1.5fr !important;
            gap: 1rem !important;
          }
          
          /* First column: left aligned */
          #invoice-header .grid-cols-3 > div:first-child {
            text-align: left !important;
          }
          
          /* Middle column: centered */
          #invoice-header .grid-cols-3 > div:nth-child(2) {
            text-align: center !important;
          }
          
          /* Last column: right aligned */
          #invoice-header .grid-cols-3 > div:last-child {
            text-align: right !important;
          }
          
          /* Center invoice title and number in middle column */
          #invoice-header .text-center {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            margin-left: auto !important;
            margin-right: auto !important;
            padding-left: 20px !important;
          }
          
          #invoice-header .text-center .invoice-title,
          #invoice-header .text-center h1,
          #invoice-header .text-center p {
            text-align: center !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          
          /* Add spacing between client section and invoice details table */
          .client-section + div {
            margin-top: 0.8rem !important;
            padding-top: 0.5rem !important;
          }
          
          /* Consistent spacing for service items table section - same as client section */
          .p-6.border-b.border-gray-200:has(table#service-items-table) {
            margin-top: 0.8rem !important;
            padding-top: 0.8rem !important;
            margin-bottom: 0.8rem !important;
            padding-bottom: 0.8rem !important;
          }
          
          .invoice-meta-print .invoice-title,
          .invoice-meta-print h1,
          .invoice-meta-print p {
            text-align: center !important;
          }
          
          /* Prevent invoice number from wrapping to next line */
          .invoice-meta-print p {
            white-space: nowrap !important;
            display: inline-block !important;
          }
          
          /* Keep invoice title and number on same visual line */
          .invoice-meta-print {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
          }
          
          /* Logo size */
          img {
            max-height: 80px !important;
            width: auto !important;
            object-fit: contain !important;
          }
          
          /* Invoice title */
          .invoice-title {
            font-size: 1.8rem !important;
            font-weight: bold;
            color: #E53935;
            text-align: left !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Company info - smaller font */
          .text-sm {
            font-size: 0.7rem !important;
            line-height: 1.2 !important;
          }
          
          .text-lg {
            font-size: 0.85rem !important;
          }
          
          /* Table adjustments */
          table {
            width: 100% !important;
            margin-bottom: 0.5rem !important;
            page-break-inside: avoid !important;
          }
          
          /* Fix invoice details table column widths in print */
          table[style*="table-layout: fixed"] {
            table-layout: fixed !important;
          }
          
          /* Force column widths for invoice details table in print */
          table[style*="table-layout: fixed"] th:nth-child(1),
          table[style*="table-layout: fixed"] td:nth-child(1) {
            width: 25% !important;
            min-width: 25% !important;
            max-width: 25% !important;
          }
          
          table[style*="table-layout: fixed"] th:nth-child(2),
          table[style*="table-layout: fixed"] td:nth-child(2) {
            width: 25% !important;
            min-width: 25% !important;
            max-width: 25% !important;
          }
          
          table[style*="table-layout: fixed"] th:nth-child(3),
          table[style*="table-layout: fixed"] td:nth-child(3) {
            width: 30% !important;
            min-width: 30% !important;
            max-width: 30% !important;
          }
          
          table[style*="table-layout: fixed"] th:nth-child(4),
          table[style*="table-layout: fixed"] td:nth-child(4) {
            width: 20% !important;
            min-width: 20% !important;
            max-width: 20% !important;
          }
          
          /* Ensure edit inputs don't affect column widths in print */
          table[style*="table-layout: fixed"] .editable-field,
          table[style*="table-layout: fixed"] .editable-amount {
            max-width: 100% !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          
          /* Prevent survey location inputs from expanding column */
          table[style*="table-layout: fixed"] td:nth-child(3) .editable-field {
            max-width: calc(100% - 10px) !important;
          }
          
          table[style*="table-layout: fixed"] td:nth-child(3) .flex {
            max-width: 100% !important;
            justify-content: center !important;
          }
          
          /* Ensure Description and Amount columns maintain 70/30 ratio in print */
          #service-items-table {
            table-layout: fixed !important;
          }
          
          #service-items-table th:first-child,
          #service-items-table td:first-child {
            width: 70% !important;
          }
          
          #service-items-table th:last-child,
          #service-items-table td:last-child {
            width: 30% !important;
          }
          
          .table-header {
          background-color: #E53935 !important;
          color: white !important;
            font-weight: 600 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          th, td {
            padding: 0.3rem 0.5rem !important;
            font-size: 0.75rem !important;
            line-height: 1.2 !important;
          }
          
          /* Service items table - very small height for description rows */
          #service-items-table td,
          #totals-section td {
            font-size: 0.8rem !important;
          }
          
          #service-items-body td {
            padding: 0.15rem !important;
            line-height: 1.2 !important;
          }
          
          #service-items-body tr {
            height: auto !important;
            min-height: auto !important;
          }
          
          /* Total Amount Due row - same height as description rows in print */
          #totals-section tr:first-child th,
          #totals-section tr:first-child td {
            padding: 0.15rem !important;
            line-height: 1.2 !important;
          }
          
          /* Tax explanation - right aligned in first column in print */
          #totals-section tr:last-child td:first-child {
            text-align: right !important;
          }
          
          #totals-section tr:not(:first-child) td,
          #totals-section tr:not(:first-child) th {
            padding: 0.15rem !important;
          }
          
          /* Totals section - Light pastel #E53935 */
          tr[style*="rgba(229, 57, 53, 0.1)"] {
            background-color: rgba(229, 57, 53, 0.1) !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Total Amount Due row - same height as description rows */
          #totals-section tr:first-child th,
          #totals-section tr:first-child td {
            padding: 0.15rem !important;
            line-height: 1.2 !important;
          }
          
          /* Tax explanation - right aligned in first column */
          #totals-section tr:last-child td:first-child {
            text-align: right !important;
          }
          
          .text-lg {
            font-size: 0.9rem !important;
          }
          
          /* Recovery table - smaller with dashed borders */
          .text-sm table {
            font-size: 0.7rem !important;
          }
          
          .text-sm th,
          .text-sm td {
            padding: 0.25rem !important;
            font-size: 0.7rem !important;
          }
          
          /* Ensure dashed borders in print for recovery table */
          table.text-sm th,
          table.text-sm td {
            border: 1px dashed #999 !important;
          }
          
          /* Recovery table header background - match Total Amount Due background */
          .recovery-table th:first-child {
            background-color: rgba(229, 57, 53, 0.1) !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Recovery table first column cells - match Total Amount Due background */
          .recovery-table tbody th:first-child {
            background-color: rgba(229, 57, 53, 0.1) !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Bank details card styling for print */
          .bg-gray-50 .bg-white {
            background-color: white !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 0.5rem !important;
            padding: 0.75rem !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            page-break-inside: avoid !important;
          }
          
          /* Bank details spacing for print */
          .bg-gray-50 .space-y-3 > * + *,
          .bg-gray-50 .space-y-4 > * + * {
            margin-top: 0.5rem !important;
          }
          
          /* Bank details text sizes for print */
          .bg-gray-50 .text-xs {
            font-size: 0.65rem !important;
          }
          
          .bg-gray-50 .text-sm {
            font-size: 0.75rem !important;
          }
          
          /* Bank details grid for print */
          .bg-gray-50 .grid {
            gap: 0.75rem !important;
          }
          
          /* Bank details */
          .bg-gray-50 {
            background-color: #f9fafb !important;
            padding: 0.5rem !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Bank details rounded cards background for print */
          .bg-gray-50 .bg-gray-50 {
            background-color: #f9fafb !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Payment reminder - no pink background */
          .bg-red-50,
          div[style*="bg-red-50"] {
            background-color: white !important;
            padding: 0.5rem !important;
            margin-top: 0.5rem !important;
          }
          
          .text-red-700 {
            color: #b91c1c !important;
            font-size: 0.7rem !important;
            line-height: 1.3 !important;
          }
          
          .text-xs {
            font-size: 0.65rem !important;
          }
          
          /* Remove gaps between sections */
          .border-b,
          .border-b-2 {
            margin-bottom: 0 !important;
          }
          
          /* Grid adjustments */
          .grid {
            gap: 0.5rem !important;
          }
          
          /* Reduce vertical spacing in all sections */
          .gap-4 {
            gap: 0.5rem !important;
          }
          
          /* Compact spacing for client info */
          .flex.items-start.gap-4 {
            gap: 0.5rem !important;
            margin-bottom: 0.2rem !important;
          }
          
          /* Align client info labels and values to left, use same font size as table */
          .flex.items-start.gap-4 > span.font-semibold {
            font-size: 0.75rem !important;
            width: auto !important;
            min-width: 60px !important;
            text-align: left !important;
            margin-right: 0.3rem !important;
          }
          
          .flex.items-start.gap-4 > span:not(.font-semibold),
          .flex.items-start.gap-4 > input,
          .flex.items-start.gap-4 > textarea {
            font-size: 0.75rem !important;
            text-align: left !important;
          }
          
          /* Client address and email alignment */
          .flex.items-start.gap-4 .whitespace-pre-line {
            font-size: 0.75rem !important;
            text-align: left !important;
          }
          
          /* Issue Date and Due Date - same font size as table */
          .col-span-4.text-right p {
            font-size: 0.75rem !important;
          }
          
          .col-span-4.text-right p strong {
            font-size: 0.75rem !important;
          }
          
          /* Add spacing between Issue Date and Due Date in print */
          .col-span-4.text-right p:first-child {
            margin-bottom: 0.8rem !important;
          }
          
          .col-span-4.text-right p:last-child {
            margin-top: 0.5rem !important;
          }
          
          /* Prevent page breaks inside important sections */
          .border-b-2,
          .border-b,
          table,
          .bg-gray-50,
          .bg-red-50 {
            page-break-inside: avoid !important;
          }
          
          /* Ensure invoice fits on one page */
          .invoice-page {
            height: auto !important;
            min-height: auto !important;
          }
          
          /* Keep invoice footer group together - prevent page breaks */
          .invoice-footer-group {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            display: block !important;
          }
          
          /* Hide edit controls in print */
          .edit-controls,
          .edit-mode-indicator {
            display: none !important;
          }
          
          /* Remove edit borders in print */
          .editable-field,
          .editable-amount,
          .editable-description {
            border: none !important;
            background: transparent !important;
          }
          
          /* Fix Bank Details and below to bottom for print */
          .bg-white {
            display: flex !important;
            flex-direction: column !important;
            min-height: auto !important;
          }
          
          .invoice-content-wrapper {
            display: flex !important;
            flex-direction: column !important;
            min-height: calc(100vh - 2rem) !important;
            justify-content: space-between !important;
          }
          
          .invoice-footer-group {
            margin-top: auto !important;
            flex-shrink: 0 !important;
          }
          
          .invoice-footer-section {
            margin-top: 0 !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Ensure footer sections stay together with no gaps */
          .invoice-footer-group .invoice-footer-section {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            padding-top: 0.3rem !important;
            padding-bottom: 0.3rem !important;
          }
          
          .invoice-footer-group .invoice-footer-section:first-child {
            padding-top: 0.4rem !important;
          }
          
          .invoice-footer-group .invoice-footer-section:last-child {
            padding-bottom: 0.4rem !important;
          }
          
          /* Remove all unnecessary whitespace */
          * {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
          }
          
          .p-6 {
            padding: 0.3rem !important;
          }
          
          /* Make sure tables are compact */
          table {
            border-collapse: collapse !important;
          }
          
          /* Reduce Bank Details padding in print */
          .invoice-footer-group .bg-gray-50 {
            padding: 0.3rem !important;
            padding-left: 0 !important;
          }
          
          /* Ensure Bank Details container doesn't add left padding */
          .invoice-footer-group .bg-gray-50 > * {
            margin-left: 0 !important;
          }
          
          .invoice-footer-group .bg-white {
            padding: 0 !important;
          }
          
          /* Reduce Bank Details card padding */
          .invoice-footer-group .px-5 {
            padding-left: 0 !important;
            padding-right: 0.5rem !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          
          /* Ensure currency account cards align properly in print */
          .invoice-footer-group .bg-white.rounded-lg {
            padding-left: 0 !important;
            padding-right: 0.5rem !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          
          /* Ensure first row aligns left in print */
          .invoice-footer-group .bg-white .flex:first-child {
            padding-left: 0 !important;
            margin-left: 0 !important;
          }
          
          /* Ensure Bank Information card first row aligns properly */
          .invoice-footer-group .bg-white.rounded-lg .space-y-0 > .flex:first-child {
            padding-left: 0 !important;
            margin-left: 0 !important;
          }
          
          /* Remove any left padding/margin from first elements in cards */
          .invoice-footer-group .bg-white .space-y-0 > *:first-child {
            margin-left: 0 !important;
            padding-left: 0 !important;
          }
          
          /* Ensure all flex rows in cards start from left edge */
          .invoice-footer-group .bg-white .flex {
            margin-left: 0 !important;
            padding-left: 0 !important;
          }
          
          /* Bank details left column labels - align perfectly to left */
          .invoice-footer-group .w-36 {
            padding-left: 0 !important;
            margin-left: 0 !important;
            text-align: left !important;
          }
          
          /* Increase width of second column in bank details to prevent wrapping */
          .invoice-footer-group .flex-1 {
            min-width: 65% !important;
            width: 65% !important;
          }
          
          /* Add space between label and grid columns in bank details */
          .invoice-footer-group .flex-1.grid.grid-cols-2 {
            margin-left: 0.5rem !important;
          }
          
          /* Prevent bank address from wrapping in print */
          .invoice-footer-group .flex-1.grid.grid-cols-2 > div {
            white-space: nowrap !important;
            overflow: visible !important;
          }
          
          /* Reduce gap in bank address grid to give more space */
          .invoice-footer-group .flex-1.grid.grid-cols-2 {
            gap: 0.75rem !important;
          }
          
          /* Make beneficiary name larger in print */
          .invoice-footer-group .flex-1.text-center.font-bold {
            font-size: 0.75rem !important;
          }
          
          /* Bank details container - remove all left padding */
          .invoice-footer-group .bg-white.rounded-lg {
            padding-left: 0 !important;
          }
          
          .invoice-footer-group .bg-white.rounded-lg > * {
            padding-left: 0 !important;
            margin-left: 0 !important;
          }
          
          .invoice-footer-group .pt-5 {
            padding-top: 0.3rem !important;
          }
          
          .invoice-footer-group .pb-3 {
            padding-bottom: 0.2rem !important;
          }
          
          /* Reduce row padding in Bank Details */
          .invoice-footer-group .py-2 {
            padding-top: 0.15rem !important;
            padding-bottom: 0.15rem !important;
          }
          
          /* Reduce spacing in Bank Details */
          .invoice-footer-group .space-y-0 > * + * {
            margin-top: 0 !important;
          }
          
          /* Bank Details grid in print - give more width to value column and signature area */
          .invoice-footer-group .bank-details-row {
            grid-template-columns: 18% 42% 40% !important;
          }
          
          /* Bank detail values (including bank address) - smaller font and single line in PDF */
          .invoice-footer-group .bank-detail-value {
            white-space: nowrap !important;
            font-size: 0.6rem !important;
          }
          
          /* Signature image in PDF - reduce size, keep right aligned, moved down to avoid overlap */
          .invoice-footer-group img[alt="Signature"] {
            height: 130px !important;
            max-height: 130px !important;
          }
          
          /* Signature container positioning in PDF - move down to avoid overlap with Address */
          .invoice-footer-group .bg-white.rounded-lg[style*="position: relative"] > div[style*="position: absolute"] {
            top: 65% !important;
          }
          
          /* Reduce Bank Details title margin */
          .invoice-footer-group h6 {
            margin-bottom: 0.3rem !important;
            font-size: 0.75rem !important;
          }
          
          /* Reduce Payment Reminder padding */
          .invoice-footer-group .text-red-700 {
            font-size: 0.65rem !important;
            line-height: 1.2 !important;
            margin-bottom: 0.2rem !important;
          }
          
          /* UAE invoice - increase column width and reduce company name font size */
          .invoice-footer-group .grid.grid-cols-2 {
            gap: 1rem !important;
          }
          
          .invoice-footer-group .flex-1.text-center {
            font-size: 0.65rem !important;
            word-break: break-word !important;
          }
          
          /* Ensure invoice fits on one page - reduce all spacing */
          .invoice-page {
            height: auto !important;
            max-height: 100vh !important;
            overflow: hidden !important;
          }
          
          /* Force single page - prevent page breaks */
          .container {
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
          
          .bg-white.shadow-lg {
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            max-height: 100vh !important;
          }
          
          /* Reduce header padding */
          #invoice-header {
            padding: 0.4rem !important;
          }
          
          /* Reduce all section padding */
          .border-b {
            padding: 0.3rem !important;
          }
        }
        
        /* Screen styles */
        .invoice-title {
          font-size: 2.5rem;
          font-weight: bold;
          color: #E53935;
          text-align: center;
          margin-left: auto !important;
          margin-right: auto !important;
        }
        
        /* Invoice number centered */
        #invoice-header .text-center {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          margin-left: auto !important;
          margin-right: auto !important;
          padding-left: 20px !important;
        }
        
        #invoice-header .text-center p {
          text-align: center !important;
          margin-left: auto !important;
          margin-right: auto !important;
        }
        .table-header {
          background-color: #E53935;
          color: white;
          font-weight: 600;
        }
        
        /* Client info and date sections - same font size as table */
        .flex.items-start.gap-4 > span.font-semibold,
        .flex.items-start.gap-4 > span:not(.font-semibold),
        .flex.items-start.gap-4 .whitespace-pre-line {
          font-size: 0.875rem;
        }
        
        .col-span-4.text-right p {
          font-size: 0.875rem;
        }
        
        .col-span-4.text-right p strong {
          font-size: 0.875rem;
        }
        
        /* Recovery table - dashed borders for screen view */
        table.text-sm th,
        table.text-sm td {
          border: 1px dashed #ccc !important;
        }
        
        /* Recovery table header background for screen - match Total Amount Due background */
        .recovery-table th:first-child {
          background-color: rgba(229, 57, 53, 0.1) !important;
        }
        
        /* Recovery table first column cells - match Total Amount Due background */
        .recovery-table tbody th:first-child {
          background-color: rgba(229, 57, 53, 0.1) !important;
        }
        
        /* Bank details card styling for screen */
        .bg-gray-50 .bg-white {
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        }
        
        /* Recovery table - increase width of LSFO/HSFO/MGO column for screen */
        table.text-sm th:first-child {
          width: 20% !important;
          min-width: 20% !important;
        }
        
        /* Description rows - very small height */
        #service-items-body td {
          padding: 0.15rem !important;
          line-height: 1.2 !important;
        }
        
        #service-items-body tr {
          height: auto !important;
          min-height: auto !important;
        }
        
        /* Total Amount Due - same height as description rows */
        #totals-section tr:first-child th,
        #totals-section tr:first-child td {
          padding: 0.15rem !important;
          line-height: 1.2 !important;
        }
        
        /* Tax explanation - right aligned in first column */
        #totals-section tr:last-child td:first-child {
          text-align: right !important;
        }
        
        /* Total Amount Due background color - Light pastel #E53935 */
        tr[style*="rgba(229, 57, 53, 0.1)"] {
          background-color: rgba(229, 57, 53, 0.1) !important;
        }
        
        /* Ensure Description and Amount columns maintain 70/30 ratio on screen */
        #service-items-table {
          table-layout: fixed !important;
        }
        
        #service-items-table th:first-child,
        #service-items-table td:first-child {
          width: 70% !important;
        }
        
        #service-items-table th:last-child,
        #service-items-table td:last-child {
          width: 30% !important;
        }
        
        /* Fix Bank Details and below to bottom for screen - simpler approach */
        .invoice-page {
          position: relative !important;
          z-index: 0 !important;
        }
        
        .invoice-page .container {
          position: relative !important;
          z-index: 0 !important;
        }
        
        .invoice-page .bg-white {
          position: relative !important;
          z-index: 0 !important;
        }
        
        /* Use a wrapper div approach - wrap content sections */
        .invoice-content-wrapper {
          display: flex !important;
          flex-direction: column !important;
          min-height: calc(100vh - 12rem) !important;
          justify-content: space-between !important;
        }
        
        .invoice-footer-group {
          margin-top: auto !important;
        }
        
        .invoice-footer-section {
          margin-top: 0 !important;
        }
        
        /* Ensure footer sections stay together */
        .invoice-footer-group .invoice-footer-section ~ .invoice-footer-section {
          margin-top: 0 !important;
        }
        
        /* Edit mode styles - auto-size to content */
        .editable-field:not(textarea),
        .editable-amount:not(textarea),
        .editable-description:not(textarea) {
          border: 1px dashed transparent;
          padding: 2px 5px;
          transition: all 0.2s ease;
          min-height: 1em;
          border-radius: 3px;
          width: auto !important;
          display: inline-block !important;
          min-width: 50px;
          max-width: 100%;
          vertical-align: baseline;
          margin: 0;
          line-height: inherit;
        }
        
        /* Ensure edit inputs don't shift layout - maintain same position as text */
        td .editable-field:not(textarea),
        td .editable-amount:not(textarea),
        td .editable-description:not(textarea) {
          text-align: inherit;
        }
        
        /* Keep survey location inputs centered and together */
        .flex.gap-1.items-center.justify-center {
          justify-content: center;
          align-items: center;
        }
        
        .flex.gap-1.items-center.justify-center .editable-field {
          margin: 0;
        }
        
        /* Textarea should match original text height exactly */
        textarea.editable-field {
          width: auto !important;
          display: inline-block !important;
          border: 1px dashed transparent;
          padding: 0.25rem 0.5rem;
          transition: all 0.2s ease;
          border-radius: 3px;
          resize: none !important;
          overflow: hidden;
          line-height: 1.5rem;
          vertical-align: top;
          margin: 0;
        }
        
        /* Ensure textarea matches span height in edit mode - no layout shift */
        .flex.items-start.gap-4 textarea.editable-field {
          height: auto !important;
          min-height: 1.5rem;
          max-height: none;
        }
        
        /* Match span display properties */
        .flex.items-start.gap-4 span.whitespace-pre-line {
          display: inline-block;
          line-height: 1.5rem;
          vertical-align: top;
        }
        
        /* Ensure no layout shift when switching between span and textarea */
        .flex.items-start.gap-4 > span.whitespace-pre-line,
        .flex.items-start.gap-4 > textarea.editable-field {
          margin: 0;
          padding: 0;
          vertical-align: top;
        }
        
        /* Hide textarea resize handle in print */
        @media print {
          textarea {
            resize: none !important;
            overflow: hidden !important;
          }
          
          textarea::-webkit-resizer {
            display: none !important;
          }
        }
        
        /* Override w-full and flex-1 for editable fields (except textarea) */
        .editable-field.w-full:not(textarea),
        .editable-amount.w-full:not(textarea),
        .editable-description.w-full:not(textarea) {
          width: auto !important;
          max-width: 100%;
        }
        
        .editable-field.flex-1:not(textarea),
        .editable-amount.flex-1:not(textarea),
        .editable-description.flex-1:not(textarea) {
          width: auto !important;
          flex: none !important;
          max-width: 100%;
        }
        
        .editable-field:hover,
        .editable-amount:hover,
        .editable-description:hover {
          border-color: #E53935;
          background-color: #f0f9fa;
          cursor: text;
        }
        
        .editable-field:focus,
        .editable-amount:focus,
        .editable-description:focus {
          border-color: #E53935;
          background-color: #fff;
          outline: none;
        }
        
        /* Editable title styling */
        .editable-title {
          transition: all 0.2s ease;
        }
        
        .editable-title:hover {
          background-color: #fff9f0 !important;
        }
        
        .editable-title:focus {
          background-color: #fff !important;
          outline: 2px solid #3b82f6 !important;
          outline-offset: 2px !important;
        }
        
        /* Editable content styling (for all contentEditable fields) */
        .editable-content {
          transition: all 0.2s ease;
        }
        
        .editable-content:hover {
          background-color: #fff9f0 !important;
        }
        
        .editable-content:focus {
          background-color: #fff !important;
          outline: 2px solid #3b82f6 !important;
          outline-offset: 2px !important;
        }
        
        /* Ensure client section editable fields maintain font size */
        .client-section .editable-content {
          font-size: 0.875rem !important;
        }
        
        /* Client section non-editable spans should also maintain font size */
        .client-section .flex.items-start.gap-4 > span:not(.font-semibold) {
          font-size: 0.875rem !important;
        }
        
        @media print {
          .editable-title,
          .editable-content {
            outline: none !important;
            padding: 0 !important;
          }
        }
        
        .edit-mode-indicator {
          position: fixed;
          top: 20px;
          right: 20px;
          background-color: #E53935;
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 600;
          z-index: 1000;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }
        
        .edit-controls {
          position: fixed;
          bottom: 20px;
          right: 20px;
          z-index: 1000;
          display: flex;
          gap: 10px;
        }
        
        .edit-controls button {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }
        
        .edit-controls button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        
        .btn-edit {
          background-color: #E53935;
          color: white;
        }
        
        .btn-edit:hover {
          background-color: #0f7a95;
        }
        
        .btn-reset {
          background-color: #6c757d;
          color: white;
        }
        
        .btn-reset:hover {
          background-color: #5a6268;
        }
        
        /* Non-editable fields (bank details) */
        .non-editable {
          pointer-events: none;
          user-select: none;
        }

        /* Placeholder styles for contentEditable */
        .editable-content[placeholder]:empty:before {
          content: attr(placeholder);
          color: #9ca3af;
          font-style: italic;
        }

        .editable-content[placeholder]:empty:focus:before {
          content: '';
        }

        /* Neon glow effects for teal elements */
        .neon-teal-glow {
          box-shadow: 0 0 10px rgba(20, 184, 166, 0.5), 0 0 20px rgba(20, 184, 166, 0.3), 0 0 30px rgba(20, 184, 166, 0.1);
        }

        .neon-teal-glow:hover {
          box-shadow: 0 0 15px rgba(20, 184, 166, 0.7), 0 0 25px rgba(20, 184, 166, 0.5), 0 0 35px rgba(20, 184, 166, 0.3);
        }

        /* Enhanced neon for buttons */
        .bg-gradient-to-r.from-teal-500.to-teal-600:hover {
          box-shadow: 0 0 20px rgba(20, 184, 166, 0.6), 0 0 30px rgba(20, 184, 166, 0.4), 0 0 40px rgba(20, 184, 166, 0.2);
        }

        /* Enhanced neon for borders */
        .border-teal-500:hover {
          box-shadow: 0 0 15px rgba(20, 184, 166, 0.4), 0 0 25px rgba(20, 184, 166, 0.2);
        }
        
      `}</style>
      
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          {/* Header */}
          <div className="border-b-2 p-6" id="invoice-header" style={{ borderColor: '#E53935' }}>
            <div className="grid gap-4 items-center" style={{ gridTemplateColumns: '1.3fr 0.7fr 1.5fr' }}>
              {/* First column: Logo - Left aligned */}
              <div className="text-left">
                <img src="/images/logo.jpeg" alt="JNJ Logo" className="h-24 object-contain" />
              </div>
              {/* Middle column: Invoice Title and Number - Centered */}
              <div className="text-center flex flex-col items-center justify-center" style={{ paddingLeft: '20px' }}>
                    {isCellActive('invoice_title') ? (
                      <div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const newValue = e.target.textContent.trim() || 'INVOICE'
                          handleDetailEdit('invoice_title', newValue)
                        setActiveEditCell(null)
                        }}
                        onInput={(e) => {
                          // Auto-resize width based on content with exact font styling
                          const tempSpan = document.createElement('span')
                          tempSpan.className = 'invoice-title mb-0 text-center'
                          tempSpan.style.position = 'absolute'
                          tempSpan.style.visibility = 'hidden'
                          tempSpan.style.whiteSpace = 'nowrap'
                          tempSpan.style.fontSize = '2.5rem'
                          tempSpan.style.fontWeight = 'bold'
                          tempSpan.style.fontFamily = 'inherit'
                          tempSpan.textContent = e.target.textContent || 'INVOICE'
                          document.body.appendChild(tempSpan)
                          const width = tempSpan.offsetWidth
                          document.body.removeChild(tempSpan)
                          e.target.style.width = `${Math.max(width + 20, titleWidth || 150)}px`
                        }}
                        className="invoice-title mb-0 text-center editable-title"
                        style={{ 
                          color: '#E53935', 
                          fontWeight: 'bold', 
                          fontSize: '2.5rem',
                          borderRadius: '4px',
                          padding: '2px 4px',
                          width: titleWidth ? `${titleWidth}px` : 'auto',
                          minWidth: titleWidth ? `${titleWidth}px` : '150px',
                          cursor: 'text'
                        }}
                        ref={titleMeasureRef}
                      >
                        {getDetailValue('invoice_title') || 'INVOICE'}
                      </div>
                    ) : (
                      <h1
                        className="invoice-title mb-0 text-center cursor-text hover:bg-amber-50/60 rounded"
                        ref={titleMeasureRef}
                        style={{ margin: '0 auto', textAlign: 'center' }}
                        role="button"
                        tabIndex={0}
                        onClick={() => activateCell('invoice_title')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            activateCell('invoice_title')
                          }
                        }}
                      >
                        {getDetailValue('invoice_title') || 'INVOICE'}
                      </h1>
                    )}
                    {isCellActive('header_invoice_number') ? (
                      <div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const newValue = e.target.textContent.trim()
                          handleDetailEdit('invoice_number', newValue)
                        setActiveEditCell(null)
                        }}
                        onInput={(e) => {
                          const text = e.target.textContent || ''
                          const width = measureTextWidth(text, {
                            fontSize: '1.125rem',
                            fontFamily: window.getComputedStyle(e.target).fontFamily
                          })
                          e.target.style.width = `${Math.max(80, Math.min(width + 20, 200))}px`
                        }}
                        className="editable-content text-center text-gray-600 text-lg mt-1"
                        style={{
                          fontSize: '1.125rem',
                          color: '#4b5563',
                          minWidth: '80px',
                          width: 'auto'
                        }}
                      >
                        {getDetailValue('invoice_number')}
                      </div>
                    ) : (
                      wrapClickToEdit(
                        'header_invoice_number',
                        getDetailValue('invoice_number'),
                        'text-gray-600 text-lg mt-1 text-center block w-full'
                      )
                    )}
              </div>
              {/* Last column: Company Info - Right aligned */}
              <div className="text-right">
                {/* Company Selector */}
                {companyInfoList.length > 0 && (
                  <div className="mb-3 no-print">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Select Company:</label>
                    <select
                      value={selectedCompanyId || ''}
                      onChange={(e) => handleCompanyChange(Number(e.target.value))}
                      className="w-full px-3 py-1.5 text-sm border-2 border-teal-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-600 bg-gradient-to-r from-teal-50 to-teal-100 hover:from-teal-100 hover:to-teal-200 transition-all duration-200 font-medium text-teal-700 shadow-md hover:shadow-lg neon-teal-glow"
                      style={{ maxWidth: '250px', marginLeft: 'auto' }}
                    >
                      <option value="">-- Select Company --</option>
                      {companyInfoList.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.company_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Only show company info if selected */}
                {selectedCompanyId && company_info && company_info.name ? (
                  <>
                    <div className="font-semibold text-gray-900">{company_info.name}</div>
                    <div className="text-sm text-gray-600 whitespace-pre-line">{company_info.address}</div>
                    {company_info.tel && <div className="text-sm text-gray-600">{company_info.tel}</div>}
                  </>
                ) : (
                  <div className="text-sm text-gray-400 italic">No company selected</div>
                )}
              </div>
            </div>
          </div>

          {/* Content Wrapper */}
          <div className="invoice-content-wrapper">
          {/* Spacer for additional space between header and client section */}
          <div style={{ height: '2rem' }} className="no-print"></div>
          
          {/* Client and Date Info */}
          <div className="p-6 border-b border-gray-200 client-section" style={{ marginTop: '0.8rem', paddingTop: '0.8rem', marginBottom: '0.8rem', paddingBottom: '0.8rem' }}>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-8">
                <div className="flex items-start gap-4 mb-2">
                  <span className="font-semibold w-24">Issued For:</span>
                  {isCellActive('client_name') ? (
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => {
                        const newValue = e.target.textContent.trim()
                        handleDetailEdit('client_name', newValue)
                      setActiveEditCell(null)
                      }}
                      onInput={(e) => {
                        const text = e.target.textContent || ''
                        const width = measureTextWidth(text, {
                          fontFamily: window.getComputedStyle(e.target).fontFamily
                        })
                        e.target.style.width = `${Math.max(100, Math.min(width + 20, 400))}px`
                      }}
                      className="editable-content"
                      style={{
                        fontSize: '0.875rem',
                        minWidth: '100px',
                        width: 'auto'
                      }}
                    >
                      {getDetailValue('client_name')}
                    </div>
                  ) : (
                    wrapClickToEdit('client_name', getDetailValue('client_name'))
                  )}
                </div>
                {client_address && (
                  <div className="flex items-start gap-4 mb-2">
                    <span className="font-semibold w-24" style={{ visibility: 'hidden' }}>Address:</span>
                    {isCellActive('client_address') ? (
                      <div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const newValue = e.target.textContent.trim()
                          handleDetailEdit('client_address', newValue)
                        setActiveEditCell(null)
                        }}
                        onInput={(e) => {
                          // Auto-resize height based on content
                          e.target.style.height = 'auto'
                          e.target.style.height = `${Math.max(e.target.scrollHeight, 24)}px`
                          // Auto-resize width based on content
                          const text = e.target.textContent || ''
                          const width = measureTextWidth(text, {
                            fontFamily: window.getComputedStyle(e.target).fontFamily,
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-line'
                          })
                          e.target.style.width = `${Math.max(200, Math.min(width + 20, 400))}px`
                        }}
                        className="editable-content whitespace-pre-line"
                        style={{
                          fontSize: '0.875rem',
                          lineHeight: '24px',
                          minWidth: '200px',
                          width: 'auto',
                          minHeight: '24px',
                          display: 'inline-block',
                          verticalAlign: 'top',
                          margin: 0,
                          padding: '2px 4px',
                          boxSizing: 'border-box'
                        }}
                        ref={addressSpanRef}
                      >
                        {getDetailValue('client_address')}
                      </div>
                    ) : (
                      wrapClickToEdit(
                        'client_address',
                        <span
                          ref={addressSpanRef}
                          className="whitespace-pre-line"
                          style={{
                            fontSize: '0.875rem',
                            lineHeight: '24px',
                            display: 'inline-block',
                            verticalAlign: 'top',
                            margin: 0,
                            padding: 0,
                            minHeight: '24px',
                          }}
                        >
                          {getDetailValue('client_address')}
                        </span>
                      )
                    )}
                  </div>
                )}
                {survey.operator && (
                  <div className="flex items-start gap-4 mb-2">
                    <span className="font-semibold w-24">Attn:</span>
                  {isCellActive('operator') ? (
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => {
                        const newValue = e.target.textContent.trim()
                        handleDetailEdit('operator', newValue)
                      setActiveEditCell(null)
                      }}
                      onInput={(e) => {
                        const text = e.target.textContent || ''
                        const width = measureTextWidth(text, {
                          fontFamily: window.getComputedStyle(e.target).fontFamily
                        })
                        e.target.style.width = `${Math.max(100, Math.min(width + 20, 300))}px`
                      }}
                      className="editable-content"
                      style={{
                        fontSize: '0.875rem',
                        minWidth: '100px',
                        width: 'auto'
                      }}
                    >
                      {getDetailValue('operator')}
                    </div>
                    ) : (
                      wrapClickToEdit('operator', getDetailValue('operator'))
                    )}
                  </div>
                )}
                {client_email && (
                  <div className="flex items-start gap-4">
                    <span className="font-semibold w-24" style={{ visibility: 'hidden' }}>Email:</span>
                    {isCellActive('client_email') ? (
                      <div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const newValue = e.target.textContent.trim()
                          handleDetailEdit('client_email', newValue)
                        setActiveEditCell(null)
                        }}
                        onInput={(e) => {
                          const text = e.target.textContent || ''
                          const width = measureTextWidth(text, {
                            fontFamily: window.getComputedStyle(e.target).fontFamily
                          })
                          e.target.style.width = `${Math.max(150, Math.min(width + 20, 350))}px`
                        }}
                      className="editable-content"
                      style={{
                        fontSize: '0.875rem',
                        minWidth: '150px',
                        width: 'auto'
                      }}
                    >
                      {getDetailValue('client_email')}
                    </div>
                    ) : (
                      wrapClickToEdit('client_email', getDetailValue('client_email'))
                    )}
                  </div>
                )}
              </div>
              <div className="col-span-4 text-right">
                <p className="mb-3"><strong>Issue Date:</strong> {formatDate(survey.attendance_date)}</p>
                <p className="text-red-600 mt-2"><strong>Due Date:</strong> {formatDate(due_date)}</p>
              </div>
            </div>
          </div>

          {/* Invoice Details Table */}
          <div className="p-6 border-b border-gray-200" style={{ marginTop: '0.8rem', paddingTop: '0.5rem' }}>
            <table className="w-full border border-gray-300" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr className="table-header">
                  <th className="p-2 text-center border border-gray-300" style={{ width: '25%' }}>Invoice #</th>
                  <th className="p-2 text-center border border-gray-300" style={{ width: '25%' }}>Vessel Name</th>
                  <th className="p-2 text-center border border-gray-300" style={{ width: '30%' }}>Survey Location</th>
                  <th className="p-2 text-center border border-gray-300" style={{ width: '20%' }}>Survey Date</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 text-center border border-gray-300">
                    {isCellActive('table_invoice_number') ? (
                      <div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const newValue = e.target.textContent.trim()
                          handleDetailEdit('invoice_number', newValue)
                        setActiveEditCell(null)
                        }}
                        onInput={(e) => {
                          const text = e.target.textContent || ''
                          const width = measureTextWidth(text, {
                            fontFamily: window.getComputedStyle(e.target).fontFamily
                          })
                          e.target.style.width = `${Math.max(100, Math.min(width + 20, 200))}px`
                        }}
                        className="editable-content text-center"
                        style={{
                          minWidth: '100px',
                          width: 'auto',
                          margin: '0 auto',
                          display: 'inline-block'
                        }}
                      >
                        {getDetailValue('invoice_number')}
                      </div>
                    ) : (
                      wrapClickToEdit('table_invoice_number', getDetailValue('invoice_number'), 'text-center')
                    )}
                  </td>
                  <td className="p-2 text-center border border-gray-300">
                    <div className="flex flex-col items-center">
                      {isCellActive('vessel_name') ? (
                        <div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            handleDetailEdit('vessel_name', e.target.textContent.trim())
                            setActiveEditCell(null)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily,
                            })
                            e.target.style.width = `${Math.max(100, Math.min(width + 20, 300))}px`
                          }}
                          className="editable-content text-center mb-1"
                          style={{
                            minWidth: '100px',
                            width: 'auto',
                            margin: '0 auto',
                            display: 'inline-block',
                          }}
                        >
                          {getDetailValue('vessel_name')}
                        </div>
                      ) : (
                        wrapClickToEdit(
                          'vessel_name',
                          getDetailValue('vessel_name'),
                          'text-center mb-1 block w-full'
                        )
                      )}
                      {survey.client_ref_no && (
                        <small className="text-gray-500">
                          (
                          {isCellActive('client_ref_no') ? (
                            <div
                              contentEditable
                              suppressContentEditableWarning
                              onBlur={(e) => {
                                handleDetailEdit('client_ref_no', e.target.textContent.trim())
                                setActiveEditCell(null)
                              }}
                              onInput={(e) => {
                                const text = e.target.textContent || ''
                                const width = measureTextWidth(text, {
                                  fontSize: '0.75rem',
                                  fontFamily: window.getComputedStyle(e.target).fontFamily,
                                })
                                e.target.style.width = `${Math.max(60, Math.min(width + 10, 120))}px`
                              }}
                              className="editable-content text-center text-xs inline-block"
                              style={{
                                fontSize: '0.75rem',
                                minWidth: '60px',
                                width: 'auto',
                              }}
                            >
                              {getDetailValue('client_ref_no')}
                            </div>
                          ) : (
                            wrapClickToEdit(
                              'client_ref_no',
                              getDetailValue('client_ref_no'),
                              'text-xs'
                            )
                          )}
                          )
                        </small>
                      )}
                    </div>
                  </td>
                  <td className="p-2 text-center border border-gray-300">
                    <div className="flex gap-1 items-center justify-center">
                      {isCellActive('survey_country') ? (
                        <div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            handleDetailEdit('survey_country', e.target.textContent.trim())
                            setActiveEditCell(null)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily,
                            })
                            e.target.style.width = `${Math.max(60, Math.min(width + 20, 100))}px`
                          }}
                          className="editable-content text-center"
                          style={{ minWidth: '60px', width: 'auto', display: 'inline-block' }}
                        >
                          {getDetailValue('survey_country')}
                        </div>
                      ) : (
                        wrapClickToEdit('survey_country', getDetailValue('survey_country'))
                      )}
                      <span>/</span>
                      {isCellActive('survey_port') ? (
                        <div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            handleDetailEdit('survey_port', e.target.textContent.trim())
                            setActiveEditCell(null)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily,
                            })
                            e.target.style.width = `${Math.max(60, Math.min(width + 20, 100))}px`
                          }}
                          className="editable-content text-center"
                          style={{ minWidth: '60px', width: 'auto', display: 'inline-block' }}
                        >
                          {getDetailValue('survey_port')}
                        </div>
                      ) : (
                        wrapClickToEdit('survey_port', getDetailValue('survey_port'))
                      )}
                    </div>
                  </td>
                  <td className="p-2 text-center border border-gray-300">{formatDate(survey.attendance_date)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Invoice workspace: amounts + descriptions (screen only) */}
          <div className="p-6 bg-gray-50 border-b border-gray-200 no-print">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
              <h6 className="text-blue-700 font-semibold text-base">Invoice lines &amp; selections</h6>
              <div className="flex flex-wrap items-center gap-3">
                {hasUnsavedChanges && saveStatus !== 'saving' && (
                  <span className="text-sm text-amber-700">Unsaved changes</span>
                )}
                {saveStatus === 'saving' && (
                  <span className="text-sm text-gray-600">Saving…</span>
                )}
                {saveStatus === 'saved' && !hasUnsavedChanges && (
                  <span className="text-sm text-green-700">Saved</span>
                )}
                {saveStatus === 'error' && (
                  <span className="text-sm text-red-600">{saveError || 'Could not save'}</span>
                )}
                <button
                  type="button"
                  onClick={handleSaveInvoice}
                  disabled={saveStatus === 'saving' || !hasUnsavedChanges}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-semibold shadow-sm bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save invoice lines
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {CORE_CHARGE_LINE_KEYS.filter((itemKey) => available_items[itemKey]).map((itemKey) => {
                const checked = selectedItems[itemKey]
                const labelText =
                  available_items[itemKey]?.description?.replace(/^•\s*/, '') ||
                  itemKey.replace(/_/g, ' ')
                return (
                  <div
                    key={itemKey}
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm"
                  >
                    <label className="md:col-span-3 flex items-center gap-2 cursor-pointer select-none font-medium text-gray-800">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleItemToggle(itemKey)}
                        className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="text-sm capitalize">{labelText}</span>
                    </label>
                    <input
                      type="text"
                      aria-label={`${itemKey} description`}
                      disabled={!checked}
                      className="md:col-span-6 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                      value={getItemValue(itemKey, 'description')}
                      onChange={(e) => handleItemEdit(itemKey, 'description', e.target.value)}
                      title={
                        itemKey === 'survey_fee'
                          ? 'Survey type is added when the invoice is first opened; your edits are saved on this invoice.'
                          : undefined
                      }
                    />
                    <input
                      type="number"
                      step="0.01"
                      disabled={!checked}
                      aria-label={`${itemKey} amount`}
                      className="md:col-span-3 rounded-md border border-gray-300 px-3 py-2 text-sm text-right tabular-nums focus:border-teal-500 focus:ring-2 focus:ring-teal-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                      value={getItemValue(itemKey, 'amount')}
                      onChange={(e) => handleItemEdit(itemKey, 'amount', e.target.value)}
                    />
                  </div>
                )
              })}

              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm">
                <label className="md:col-span-3 flex items-center gap-2 cursor-pointer select-none font-medium text-gray-800">
                  <input
                    type="checkbox"
                    checked={selectedItems.discount}
                    onChange={() => handleItemToggle('discount')}
                    className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm">Discount</span>
                </label>
                <input
                  type="text"
                  aria-label="Discount description"
                  disabled={!selectedItems.discount}
                  className="md:col-span-6 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                  value={
                    editedDetails.discount_label !== undefined
                      ? editedDetails.discount_label
                      : 'Discount'
                  }
                  onChange={(e) => handleDetailEdit('discount_label', e.target.value)}
                />
                <input
                  type="number"
                  step="0.01"
                  disabled={!selectedItems.discount}
                  aria-label="Discount amount"
                  className="md:col-span-3 rounded-md border border-gray-300 px-3 py-2 text-sm text-right tabular-nums focus:border-teal-500 focus:ring-2 focus:ring-teal-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                  value={
                    editedDiscount !== null && editedDiscount !== undefined
                      ? String(editedDiscount)
                      : String(invoiceData.discount ?? '')
                  }
                  onChange={(e) => handleDiscountWorkspaceChange(e.target.value)}
                />
              </div>

              {available_items.others ? (
                <div className="md:col-span-2 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm">
                    <label className="md:col-span-3 flex items-center gap-2 cursor-pointer select-none font-medium text-gray-800">
                      <input
                        type="checkbox"
                        checked={selectedItems.others}
                        onChange={() => handleItemToggle('others')}
                        className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="text-sm capitalize">
                        {available_items.others?.description?.replace(/^•\s*/, '') ||
                          'Additional Charges'}
                      </span>
                    </label>
                    <input
                      type="text"
                      aria-label="others description"
                      disabled={!selectedItems.others}
                      className="md:col-span-6 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                      value={getItemValue('others', 'description')}
                      onChange={(e) => handleItemEdit('others', 'description', e.target.value)}
                    />
                    <div className="md:col-span-3 flex items-center gap-2">
                      <input
                        type="number"
                        step="0.01"
                        disabled={!selectedItems.others}
                        readOnly
                        title={
                          selectedItems.others
                            ? 'Total is the sum of breakdown lines below (saved as Survey.others)'
                            : undefined
                        }
                        aria-label="others total amount"
                        className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-right tabular-nums disabled:bg-gray-100 disabled:text-gray-400"
                        value={
                          selectedItems.others
                            ? sumOthersLineAmounts(emptyRows, { onlySelected: true }).toFixed(2)
                            : getItemValue('others', 'amount')
                        }
                      />
                      <button
                        type="button"
                        onClick={addOthersBreakdownLine}
                        disabled={!selectedItems.others}
                        aria-label="Add breakdown line"
                        className="shrink-0 inline-flex min-w-[4.5rem] items-center justify-center px-3 py-2 rounded-md text-sm font-semibold shadow-sm bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                  {selectedItems.others && emptyRows.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
                      {emptyRows.map((row) => (
                        <div
                          key={row.id}
                          className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-2"
                        >
                          <label className="flex shrink-0 items-center cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={row.selected !== false}
                              onChange={() => handleEmptyRowToggle(row.id)}
                              className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                              aria-label="Include this Others line on the invoice"
                            />
                          </label>
                          <input
                            type="text"
                            aria-label="Others breakdown description"
                            disabled={row.selected === false}
                            className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                            placeholder="Description"
                            value={row.description}
                            onChange={(e) => handleEmptyRowEdit(row.id, 'description', e.target.value)}
                          />
                          <input
                            type="number"
                            step="0.01"
                            aria-label="Others breakdown amount"
                            disabled={row.selected === false}
                            className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm text-right tabular-nums focus:border-teal-500 focus:ring-2 focus:ring-teal-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                            value={row.amount}
                            onChange={(e) => handleEmptyRowEdit(row.id, 'amount', e.target.value)}
                          />
                          <button
                            type="button"
                            aria-label="Remove breakdown line"
                            title="Remove this line"
                            className="shrink-0 min-w-[2.75rem] px-3 py-2 rounded-md border border-red-200 bg-red-50 text-sm font-semibold text-red-700 hover:bg-red-100"
                            onClick={() => removeOthersBreakdownLine(row.id)}
                          >
                            X
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm md:col-span-2">
                <div className="md:col-span-3 flex items-center gap-2 select-none font-medium text-gray-800">
                  <input
                    type="checkbox"
                    checked
                    disabled
                    className="w-4 h-4 rounded border-gray-300 text-teal-600 opacity-70 cursor-not-allowed"
                    aria-label="Total is always printed on this invoice"
                  />
                  <span className="text-sm">Total Amount Due</span>
                </div>
                <input
                  type="text"
                  aria-label="Total label"
                  className="md:col-span-6 rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-700"
                  disabled
                  readOnly
                  value={
                    editedDetails.total_label !== undefined
                      ? editedDetails.total_label
                      : 'Total Amount Due'
                  }
                />
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Total amount"
                  placeholder={formatChargeAlways(roundTo2(totals.total))}
                  className="md:col-span-3 rounded-md border border-gray-300 px-3 py-2 text-sm text-right tabular-nums disabled:bg-gray-100 disabled:text-gray-700"
                  disabled
                  readOnly
                  value={
                    editedTotalAmount !== null
                      ? formatChargeAlways(parseFloat(editedTotalAmount) || 0)
                      : formatChargeAlways(roundTo2(totals.total))
                  }
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-4 border-t border-gray-200">
              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  checked={selectedItems.tax}
                  onChange={() => handleItemToggle('tax')}
                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                Show tax note (8%)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  checked={selectedItems.recovery}
                  onChange={() => handleItemToggle('recovery')}
                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                Recovery table on invoice
              </label>
            </div>
          </div>

          {/* Service Items Table */}
          <div className="p-6 border-b border-gray-200" style={{ marginTop: '0.8rem', paddingTop: '0.8rem', marginBottom: '0.8rem', paddingBottom: '0.8rem' }}>
            <table id="service-items-table" className="w-full border border-gray-300">
              <thead>
                <tr className="table-header">
                  <th className="p-3 text-left border border-gray-300" style={{ width: '70%' }}>Description</th>
                  <th className="p-3 text-right border border-gray-300" style={{ width: '30%' }}>
                    <div className="flex items-center justify-end gap-2">
                      <select
                        value={currency}
                        onChange={(e) => {
                          markDirty()
                          setCurrency(e.target.value)
                        }}
                        className="no-print px-2 py-1 text-sm border border-gray-400 rounded bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-gray-200 font-medium text-red-700"
                        style={{ minWidth: '60px' }}
                      >
                        <option value="USD">$ USD</option>
                        <option value="SGD">S$ SGD</option>
                        <option value="EUR">€ EUR</option>
                      </select>
                      <span>Amount</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody id="service-items-body">
                {CORE_CHARGE_LINE_KEYS.map((itemKey) => {
                  if (!available_items[itemKey]) return null
                  if (!selectedItems[itemKey]) return null
                  const desc = getItemValue(itemKey, 'description')
                  const amt = parseFloat(getItemValue(itemKey, 'amount')) || 0
                  return (
                    <tr key={itemKey}>
                      <td className="p-1 border border-gray-300" style={{ padding: '0.15rem' }}>
                        {desc}
                      </td>
                      <td className="p-1 text-right border border-gray-300 tabular-nums" style={{ padding: '0.15rem' }}>
                        {formatCurrency(amt)}
                      </td>
                    </tr>
                  )
                })}
                {selectedItems.others && available_items.others ? (
                  <>
                    {emptyRows.length === 0 ? (
                      <tr key="others-total">
                        <td className="p-1 border border-gray-300" style={{ padding: '0.15rem' }}>
                          <strong>{getItemValue('others', 'description')}</strong>
                        </td>
                        <td className="p-1 text-right border border-gray-300 tabular-nums" style={{ padding: '0.15rem' }}>
                          {formatCurrency(sumOthersLineAmounts(emptyRows, { onlySelected: true }))}
                        </td>
                      </tr>
                    ) : null}
                    {emptyRows
                      .filter((row) => row.selected !== false)
                      .map((row) => (
                        <tr key={row.id}>
                          <td
                            className="p-1 border border-gray-300 text-gray-700 align-middle"
                            style={{ padding: '0.15rem' }}
                          >
                            {row.description}
                          </td>
                          <td className="p-1 text-right border border-gray-300 tabular-nums align-middle" style={{ padding: '0.15rem' }}>
                            {formatCurrency(parseFloat(row.amount) || 0)}
                          </td>
                        </tr>
                      ))}
                  </>
                ) : null}
                {totals.subtotal === 0 && emptyRows.length === 0 && (
                  <tr>
                    <td colSpan="2" className="p-1 text-center text-gray-500 border border-gray-300" style={{ padding: '0.15rem' }}>
                      No charge lines
                    </td>
                  </tr>
                )}
                {selectedItems.discount && (
                  <tr>
                    <td className="p-1 border border-gray-300" style={{ padding: '0.15rem' }}>
                      {editedDetails.discount_label !== undefined ? editedDetails.discount_label : 'Discount'}
                    </td>
                    <td className="p-1 text-right border border-gray-300 text-red-600 tabular-nums" style={{ padding: '0.15rem' }}>
                      -{formatChargeAlways(totals.discount)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td
                    className="p-1 border border-red-200 font-bold text-right"
                    style={{ padding: '0.15rem', backgroundColor: 'rgba(229, 57, 53, 0.08)' }}
                  >
                    {editedDetails.total_label !== undefined ? editedDetails.total_label : 'Total Amount Due'}:
                  </td>
                  <td
                    className="p-1 text-right border border-red-200 font-bold tabular-nums"
                    style={{ padding: '0.15rem', backgroundColor: 'rgba(229, 57, 53, 0.08)' }}
                  >
                    {editedTotalAmount !== null
                      ? formatChargeAlways(parseFloat(editedTotalAmount) || 0)
                      : formatChargeAlways(roundTo2(totals.total))}
                  </td>
                </tr>
                {selectedItems.tax && displayedTaxAmount > 0 && (
                  <tr className="invoice-tax-note-print" style={{ fontSize: '0.9em' }}>
                    <td className="p-1 border border-gray-300 text-right" style={{ fontStyle: 'italic', color: '#6c757d', padding: '0.15rem' }}>
                      {editedDetails.tax_label !== undefined ? editedDetails.tax_label : 'Total amount includes 8% tax'}
                    </td>
                    <td className="p-1 text-right border border-gray-300 tabular-nums" style={{ fontStyle: 'italic', color: '#6c757d', padding: '0.15rem' }}>
                      {formatCurrency(displayedTaxAmount)}
                    </td>
                  </tr>
                )}
              </tbody>

            </table>
          </div>

          {/* Recovery Table */}
          {selectedItems.recovery && (
            <div className="p-6 border-b border-gray-200 recovery-table-container">
              <table className="w-full text-sm recovery-table" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="table-header">
                    <th className="p-2" style={{ border: '1px dashed #ccc', width: '20%', backgroundColor: 'rgba(229, 57, 53, 0.1)' }}></th>
                    <th className="p-2" style={{ border: '1px dashed #ccc' }}>Logbook</th>
                    <th className="p-2" style={{ border: '1px dashed #ccc' }}>Survey</th>
                    <th className="p-2" style={{ border: '1px dashed #ccc' }}>Difference</th>
                    <th className="p-2" style={{ border: '1px dashed #ccc' }}>Price ($)</th>
                    <th className="p-2" style={{ border: '1px dashed #ccc' }}>Recovery from PCT ($)</th>
                    <th className="p-2" style={{ border: '1px dashed #ccc' }}>Recovery from MT ($)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th className="p-2 text-left" style={{ border: '1px dashed #ccc', width: '20%', backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>LSFO</th>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {renderRecoveryCell('lsfo_logbook')}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {renderRecoveryCell('lsfo_survey')}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {renderRecoveryCell('lsfo_qty')}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {renderRecoveryCell('lsfo_price', { isPrice: true })}
                    </td>
                    <td className="p-2 text-center non-editable" style={{ border: '1px dashed #ccc' }}>
                      {(() => {
                        const recovery = parseFloat(getRecoveryValue('lsfo_recovery')) || 0
                        const pct_qty = parseFloat(invoiceData?.survey?.pct_qty) || 0
                        const value = recovery && pct_qty ? recovery * pct_qty * 0.01 : 0
                        return value > 0 ? formatCurrency(value) : ''
                      })()}
                    </td>
                    <td className="p-2 text-center non-editable" style={{ border: '1px dashed #ccc' }}>
                      {(() => {
                        const qty = parseFloat(getRecoveryValue('lsfo_qty')) || 0
                        const mt_price = parseFloat(invoiceData?.survey?.mt_price) || 0
                        const value = qty && mt_price ? qty * mt_price : 0
                        return value > 0 ? formatCurrency(value) : ''
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <th className="p-2 text-left" style={{ border: '1px dashed #ccc', width: '20%', backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>HSFO</th>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {renderRecoveryCell('hsfo_logbook')}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {renderRecoveryCell('hsfo_survey')}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {renderRecoveryCell('hsfo_qty')}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {renderRecoveryCell('hsfo_price', { isPrice: true })}
                    </td>
                    <td className="p-2 text-center non-editable" style={{ border: '1px dashed #ccc' }}>
                      {(() => {
                        const recovery = parseFloat(getRecoveryValue('hsfo_recovery')) || 0
                        const pct_qty = parseFloat(invoiceData?.survey?.pct_qty) || 0
                        const value = recovery && pct_qty ? recovery * pct_qty * 0.01 : 0
                        return value > 0 ? formatCurrency(value) : ''
                      })()}
                    </td>
                    <td className="p-2 text-center non-editable" style={{ border: '1px dashed #ccc' }}>
                      {(() => {
                        const qty = parseFloat(getRecoveryValue('hsfo_qty')) || 0
                        const mt_price = parseFloat(invoiceData?.survey?.mt_price) || 0
                        const value = qty && mt_price ? qty * mt_price : 0
                        return value > 0 ? formatCurrency(value) : ''
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <th className="p-2 text-left" style={{ border: '1px dashed #ccc', width: '20%', backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>MGO</th>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {renderRecoveryCell('mgo_logbook')}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {renderRecoveryCell('mgo_survey')}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {renderRecoveryCell('mgo_qty')}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {renderRecoveryCell('mgo_price', { isPrice: true })}
                    </td>
                    <td className="p-2 text-center non-editable" style={{ border: '1px dashed #ccc' }}>
                      {(() => {
                        const recovery = parseFloat(getRecoveryValue('mgo_recovery')) || 0
                        const pct_qty = parseFloat(invoiceData?.survey?.pct_qty) || 0
                        const value = recovery && pct_qty ? recovery * pct_qty * 0.01 : 0
                        return value > 0 ? formatCurrency(value) : ''
                      })()}
                    </td>
                    <td className="p-2 text-center non-editable" style={{ border: '1px dashed #ccc' }}>
                      {(() => {
                        const qty = parseFloat(getRecoveryValue('mgo_qty')) || 0
                        const mt_price = parseFloat(invoiceData?.survey?.mt_price) || 0
                        const value = qty && mt_price ? qty * mt_price : 0
                        return value > 0 ? formatCurrency(value) : ''
                      })()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Bank Details and Footer - Keep together */}
          <div className="invoice-footer-group">
          {/* Bank Details */}
          <div className="p-6 bg-gray-50 border-b border-gray-200 invoice-footer-section">
            <h6 className="text-blue-600 mb-4 font-semibold text-lg">Bank Details</h6>
            
            {/* Bank Info Cards Selector */}
            {bankInfoList.length > 0 && (
              <div className="mb-6 no-print">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Bank Details: ({bankInfoList.length} available)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {bankInfoList.map((bank, index) => {
                    const customFields = bank.custom_fields || {}
                    // Try multiple possible field name variations
                    const bankName = customFields['Bank Name'] || customFields['bank_name'] || bank.name || 'Unnamed Bank'
                    const accountHolder = customFields['Account Holder Name'] || customFields['Account Holder'] || customFields['account_holder'] || null
                    const accountNumber = customFields['Account Number'] || customFields['account_number'] || null
                    const bankBranch = customFields['Bank Branch'] || customFields['bank_branch'] || null
                    const swiftCode = customFields['SWIFT Code'] || customFields['SWIFT'] || customFields['swift'] || null
                    
                    return (
                      <div
                        key={bank.id || index}
                        onClick={() => handleBankInfoChange(bank.id)}
                        className={`cursor-pointer rounded-lg border-2 p-4 transition-all duration-200 shadow-md hover:shadow-xl transform hover:-translate-y-1 relative overflow-hidden group neon-teal-glow ${
                          selectedBankInfoId === bank.id
                            ? 'border-teal-500 bg-gradient-to-r from-teal-50 to-teal-100 shadow-lg'
                            : 'border-teal-300 bg-gradient-to-r from-teal-50 to-teal-100 hover:border-teal-500 hover:from-teal-100 hover:to-teal-200'
                        }`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-teal-500 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 text-sm mb-1">
                              {bankName}
                            </h4>
                            {accountHolder && (
                              <p className="text-xs text-gray-600 mb-2">
                                {accountHolder}
                              </p>
                            )}
                          </div>
                          {selectedBankInfoId === bank.id && (
                            <div className="ml-2">
                              <svg className="w-5 h-5 text-teal-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 space-y-1">
                          {accountNumber && (
                            <p className="truncate font-mono">Account: {accountNumber}</p>
                          )}
                          {bankBranch && (
                            <p className="truncate">Branch: {bankBranch}</p>
                          )}
                          {swiftCode && (
                            <p className="truncate font-mono">SWIFT: {swiftCode}</p>
                          )}
                          {/* Show available custom fields if standard fields are missing */}
                          {!accountNumber && !bankBranch && !swiftCode && Object.keys(customFields).length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <p className="text-xs text-gray-400 mb-1">Available fields:</p>
                              {Object.entries(customFields).slice(0, 3).map(([key, value]) => (
                                <p key={key} className="text-xs text-gray-500 truncate">
                                  {key}: {value}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            
            {/* Debug info - remove in production */}
            {bankInfoList.length === 0 && (
              <div className="mb-6 no-print bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm">
                  No bank information found. Please check the admin panel to ensure bank information is configured.
                </p>
              </div>
            )}

            {/* Selected Bank Details Display */}
            {selectedBankInfoId && bank_info && (() => {
              console.log('Displaying bank_info:', bank_info) // Debug log
              // Display bank info in correct order with proper labels (same as old version)
              const bankDetails = [
                { label: 'Account Holder', value: bank_info.account_holder },
                { label: 'Bank Name', value: bank_info.bank_name },
                { label: 'Bank Address', value: bank_info.bank_address },
                { label: 'SWIFT', value: bank_info.swift },
                { label: 'Bank / Branch Code', value: bank_info.bank_branch_code },
                { label: 'Account Number (SGD)', value: bank_info.account_number_sgd },
                { label: 'Account Number (USD)', value: bank_info.account_number_usd }
              ].filter(item => item.value && item.value !== 'N/A' && item.value !== '-' && item.value.toString().trim() !== '')
              
              return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-5 pt-5 pb-3" style={{ paddingLeft: 0, position: 'relative' }}>
                  {/* Bank Details Rows */}
                  <div className="space-y-0">
                    {bankDetails.map((item, index) => (
                      <div 
                        key={item.label} 
                        className={`bank-details-row grid grid-cols-3 gap-4 items-center ${index < bankDetails.length - 1 ? 'border-b border-gray-200' : ''}`}
                        style={{ gridTemplateColumns: '25% 45% 30%', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
                      >
                        {/* Column 1 - Field Name */}
                        <div className="font-semibold text-gray-700 text-sm">
                          {item.label}:
                        </div>
                        {/* Column 2 - Field Value (smaller font to help keep on one line) */}
                        <div className="bank-detail-value text-gray-900 font-mono font-medium text-xs">
                          {item.value}
                        </div>
                        {/* Column 3 - Empty placeholder */}
                        <div></div>
                      </div>
                    ))}
                  </div>
                  {/* Signature - positioned absolutely to span all rows, larger size, right aligned, moved down to avoid overlap */}
                  <div 
                    className="flex items-center justify-end"
                    style={{ 
                      position: 'absolute',
                      right: '1.25rem',
                      top: '65%',
                      transform: 'translateY(-50%)',
                      height: '100%',
                      zIndex: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end'
                    }}
                  >
                    <img 
                      src={`${process.env.PUBLIC_URL || ''}/images/sign.png`}
                      alt="Signature" 
                      className="w-auto object-contain"
                      style={{ height: '120px', maxHeight: '120px' }}
                      onError={(e) => {
                        console.error('Signature image failed to load:', e.target.src);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )
            })()}

            {/* Message when no bank info is selected */}
            {!selectedBankInfoId && bankInfoList.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <p className="text-yellow-800 text-sm">Please select a bank information card above to display bank details.</p>
              </div>
            )}
          </div>

          {/* Payment Reminder */}
          <div className="p-6 border-b border-gray-200 invoice-footer-section" style={{ backgroundColor: 'white' }}>
            <div className="text-sm mb-2">
              <p className="font-semibold" style={{ color: '#E53935' }}>
                Please email <strong>"INVOICES PAID"</strong> confirmation to <strong>jnj@jnjsurveys.com</strong><br />
                with payment date, total amount, invoice number(s), and amount paid per invoice.
              </p>
            </div>
            <div className="text-xs text-red-700 mt-3 border-t border-red-200 pt-2">
              <p className="font-medium">
                A late fee of 2% per month will be applied to all overdue balances. Client is responsible for all costs of collection, including legal fees.
              </p>
            </div>
            <div className="flex justify-between text-xs text-gray-600 mt-2">
              {selectedCompanyId && company_info && company_info.email ? (
                <span>{company_info.email}</span>
              ) : null}
              {selectedCompanyId && company_info && company_info.web ? (
                <span>{company_info.web}</span>
              ) : null}
            </div>
          </div>
          </div>
          {/* End Invoice Footer Group */}

          {/* Print Button */}
          <div className="p-6 text-center no-print invoice-footer-section">
            <button
              onClick={handlePrint}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md"
            >
              <i className="bi bi-printer mr-2"></i>
              Print Invoice
            </button>
            <button
              onClick={() => navigate('/surveys')}
              className="ml-4 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg shadow-md"
            >
              Back to Surveys
            </button>
          </div>
          </div>
          {/* End Content Wrapper */}
        </div>
      </div>
    </div>
  )
}

export default Invoice
